let accessToken: string | null = null;
export function setAccessToken(t: string | null) { accessToken = t; }
export function getAccessToken() { return accessToken; }

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

let refreshPromise: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  const res = await fetch('/api/auth/refresh', {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) { accessToken = null; return false; }
  const body = await res.json();
  accessToken = body.token;
  return true;
}

function ensureRefreshed(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

export interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  timeoutMs?: number;
}

export async function apiFetch<T = unknown>(
  path: string,
  opts: ApiFetchOptions = {},
): Promise<T> {
  return doFetch<T>(path, opts, true);
}

async function doFetch<T>(path: string, opts: ApiFetchOptions, allowRefresh: boolean): Promise<T> {
  const { body, timeoutMs = 30_000, headers, ...rest } = opts;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);

  const finalHeaders = new Headers(headers);
  if (body !== undefined && !(body instanceof FormData)) {
    finalHeaders.set('Content-Type', 'application/json');
  }
  if (accessToken) finalHeaders.set('Authorization', `Bearer ${accessToken}`);

  let res: Response;
  try {
    res = await fetch(path, {
      ...rest,
      headers: finalHeaders,
      credentials: 'include',
      signal: ctrl.signal,
      body: body === undefined ? undefined
           : body instanceof FormData ? body
           : JSON.stringify(body),
    });
  } finally {
    clearTimeout(timer);
  }

  if (res.status === 401 && allowRefresh) {
    const ok = await ensureRefreshed();
    if (ok) return doFetch<T>(path, opts, false);
    throw new ApiError(401, 'Unauthorized');
  }

  if (!res.ok) {
    const errBody = await safeJson(res);
    throw new ApiError(res.status, res.statusText, errBody);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function safeJson(res: Response) {
  try { return await res.json(); } catch { return undefined; }
}
